import { useEffect, useState } from "react";
import { useParams, withRouter } from "react-router-dom";
import moment from "moment";
import { toast } from "react-toastify";
import {
  FiDownload,
  FiFile,
  FiFileText,
  FiCheck,
  FiAlertCircle,
  FiLock,
} from "react-icons/fi";
import { FaFileExcel, FaFileWord, FaFileAlt } from "react-icons/fa";

import MyYuukke from "../../../api/MyYuukke";
import ResourceDownloadApi from "../../../api/ResourceDownloadApi";

import PhoneNumberContainer from "../../../components/PhoneNumberContainer";
import { API_URL } from "../../../config/app-config";
import "./ServiceResource.css";
import ResourceRazorPay from "../../../components/ResourceRazorPay";

// ── ASSUMPTION — adjust to your real single-file download route.
const fileUrl = (fileName) => `${API_URL}auth/resource/file/${fileName}`;

// resourceFiles[i].fileName is a bare UUID with no extension, so icon/label
// must come from docType (set server-side), not from parsing the filename.


const DOC_TYPE_META = {
  PDF: { icon: FiFileText, label: "PDF Document" },
  EXCEL: { icon: FaFileExcel, label: "Excel Spreadsheet" },
  AGREEMENT: { icon: FaFileWord, label: "Word Document" },
  ODT: { icon: FaFileAlt, label: "OpenDocument Text" },
  OTHER: { icon: FiFile, label: "File" },
};

const metaFor = (docType) => DOC_TYPE_META[docType] || DOC_TYPE_META.OTHER;

const generateUUID = () =>
  ([1e7] + -1e3 + -4e3 + -8e3 + -1e3).replace(/[018]/g, (c) =>
    (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)
  );

export const ServiceResource = ({ history }) => {
  const { slug } = useParams();

  const [state, setState] = useState({
    loading: true,
    error: null,
    resource: null,
  });
  const [downloading, setDownloading] = useState(false);

  // ── Payment / payer flow state ──────────────────────────────────────────
  const [purchased, setPurchased] = useState(false); // true once we've confirmed access this visit
  const [showPayerForm, setShowPayerForm] = useState(false);
  const [payer, setPayer] = useState({ name: "", email: "", phone: "" });
  const [auditUUID, setAuditUUID] = useState(null);
  const [paymentPage, setPaymentPage] = useState(false); // mounts <RazorPay/>, opens checkout on mount
  const [paying, setPaying] = useState(false);
  const [checkingPrior, setCheckingPrior] = useState(false);
  const [savingRecord, setSavingRecord] = useState(false);

  useEffect(() => {
    if (!slug) {
      setState({ loading: false, error: "No resource specified.", resource: null });
      return;
    }

    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true, error: null }));

    MyYuukke.getResourceDownload(slug)
      .then((res) => {
        if (cancelled) return;
        const payload = res?.data?.data ?? res?.data;
        if (!payload) {
          setState({ loading: false, error: "Resource not found.", resource: null });
          return;
        }
        setState({ loading: false, error: null, resource: payload });
      })
      .catch((err) => {
        if (cancelled) return;
        const status = err?.response?.status;
        const msg =
          status === 404
            ? "This resource doesn't exist or was removed."
            : "This resource couldn't be loaded. The link may be invalid or expired.";
        setState({ loading: false, error: msg, resource: null });
      });

    return () => {
      cancelled = true;
    };
  }, [slug]);

  const { loading, error, resource } = state;
  const files = resource?.resourceFiles || [];
  const isFree = !resource?.amount || Number(resource.amount) === 0;

  // ── Saves the download/purchase record on the backend, then routes to the
  // document viewer page instead of triggering raw browser downloads here.
  //
  // NOTE: your backend controllers catch every exception internally and
  // still respond 200 OK with Response(-1, "Fail", message) — axios will
  // NOT throw on that. So success must be checked via res.data.status === 1,
  // not just "the request didn't throw."
  const finalizeDownload = async (paymentId = null) => {
    setSavingRecord(true);
    try {
      const res = await ResourceDownloadApi.saveResourceDownload({
        resourceId: resource.id,
        name: payer.name,
        email: payer.email,
        phoneNumber: payer.phone,
        paymentId,
        auditUuid: auditUUID,
      });

      if (res?.data?.status !== 1) {
        toast.warn("We couldn't save your record. Contact support if you need a reference.");
        setSavingRecord(false);
        return;
      }
    } catch (err) {
      console.error("saveResourceDownload failed:", err);
      toast.warn("We couldn't save your record. Contact support if you need a reference.");
      setSavingRecord(false);
      return;
    }
    setSavingRecord(false);
    goToViewer();
  };

  // Passes the resource straight through router state so the viewer page
  // doesn't need a second round-trip fetch on the happy path — it only
  // fetches by id itself as a fallback (e.g. on a hard refresh).
 // Redirects using the same slug this page was opened with — the viewer
  // re-fetches via the same getResourceDownload(slug) API, so no separate
  // "get by id" endpoint is needed anywhere.
  const goToViewer = () => {
    history.push(`/view-document/${slug}`, { resource });
  };

  // ── Main CTA. Both free and paid resources need payer details first
  // (the backend requires name/email/phoneNumber to save the record).
  const handleMainButtonClick = () => {
    if (!files.length || paying || checkingPrior) return;
    if (purchased) {
      goToViewer();
      return;
    }
    setShowPayerForm(true);
  };

  const handlePayerFormSubmit = async (e) => {
    e.preventDefault();
    if (!payer.name.trim() || !payer.email.trim() || !payer.phone.trim()) {
      toast.warning("Please fill all fields to continue.");
      return;
    }

    // ── Check if this email already has a record for this resource — skip
    // payment/registration for a returning buyer.
    //
    // IMPORTANT: only treat as "already downloaded" when status === 1 AND
    // data is present. The backend returns status:0 ("Not found") with
    // data:null and still HTTP 200 when nothing exists — treating the raw
    // wrapper object as truthy here is what silently skipped save() before.
    setCheckingPrior(true);
    let alreadyDownloaded = null;
    try {
      const res = await ResourceDownloadApi.checkResourceDownload(resource.id, payer.email);
      alreadyDownloaded = res?.data?.status === 1 ? res.data.data : null;
    } catch (err) {
      console.error("checkResourceDownload failed:", err);
      // fail open — let them proceed rather than blocking on a check error
    }
    setCheckingPrior(false);

    if (alreadyDownloaded) {
      toast.info("You've already unlocked this resource — taking you to your files.");
      setShowPayerForm(false);
      setPurchased(true);
      goToViewer();
      return;
    }

    setShowPayerForm(false);

    if (isFree) {
      await finalizeDownload(null);
      return;
    }

    const uuid = generateUUID();
    setAuditUUID(uuid);
    setPaying(true);
    setPaymentPage(true); // mounts <RazorPay/> → its own effect opens checkout
  };

  // Passed to <RazorPay/> as updatePaymentStatus (same contract used
  // elsewhere: ("success", paymentId) | ("dismiss") | ("failed"/other))
  const updatePaymentStatus = async (status, paymentId) => {
    setPaymentPage(false);
    setPaying(false);

    if (status === "success") {
      setPurchased(true);
      await finalizeDownload(paymentId);
      return;
    }

    if (status === "dismiss") {
      toast.info("Payment cancelled.");
      return;
    }

    toast.error("Payment failed. Please try again.");
  };

  const mainButtonLabel = () => {
    if (checkingPrior) return <><FiLock size={17} /> Checking…</>;
    if (savingRecord) return <><FiDownload size={17} /> Saving…</>;
    if (paying) return <><FiLock size={17} /> Processing payment…</>;
    if (purchased) return <><FiCheck size={17} /> View files</>;
    if (!isFree) {
      return (
        <>
          <FiLock size={17} /> Pay ₹{resource.amount} & Unlock
        </>
      );
    }
    return (
      <>
        <FiDownload size={17} />
        {files.length > 1 ? `Get all files (${files.length})` : "Get file"}
      </>
    );
  };

  return (
    <div className="svrs-stage">
      <div className="svrs-vignette" aria-hidden="true" />

      {loading && (
        <div className="svrs-ticket svrs-ticket--loading" role="status" aria-live="polite">
          <div className="svrs-spinner" aria-hidden="true" />
          <p>Opening the vault…</p>
        </div>
      )}

      {!loading && error && (
        <div className="svrs-ticket svrs-ticket--error" role="alert">
          <FiAlertCircle size={28} />
          <h2>Can't open this resource</h2>
          <p>{error}</p>
        </div>
      )}

      {!loading && !error && resource && (
        <div className="svrs-ticket">
          <div className="svrs-perf svrs-perf--top" aria-hidden="true" />

          <div className="svrs-seal">
            <span className="svrs-seal-value">
              {isFree ? "FREE" : `₹${resource.amount}`}
            </span>
            <span className="svrs-seal-label">
              {purchased || isFree ? "claimed" : "to unlock"}
            </span>
          </div>

          <header className="svrs-head">
            <span className="svrs-eyebrow">Resource unlocked</span>
            <h1 className="svrs-title">{resource.serviceName}</h1>
            {resource.description && <p className="svrs-desc">{resource.description}</p>}
            <div className="svrs-meta">
              {resource.createdAt && (
                <span>Delivered {formatArrayDate(resource.createdAt)}</span>
              )}
              <span>
                {files.length} file{files.length !== 1 ? "s" : ""}
              </span>
            </div>
          </header>

          <div className="svrs-divider">
            <span>Your files</span>
          </div>

          <ul className="svrs-file-list">
            {files.map((file, i) => {
              const { icon: Icon, label } = metaFor(file.docType);
              return (
                <li key={file.fileName + i} className="svrs-file-row">
                  <span className="svrs-file-icon">
                    <Icon size={18} />
                  </span>
                  <span className="svrs-file-name">{label}</span>
                </li>
              );
            })}
            {files.length === 0 && (
              <li className="svrs-empty">No files attached to this resource yet.</li>
            )}
          </ul>

          {/* ── Payer details — collected before either free confirm or paid checkout ── */}
          {showPayerForm && (
            <form className="svrs-payer-form" onSubmit={handlePayerFormSubmit}>
              <div className="svrs-payer-title">
                {isFree ? "Get your files" : "Unlock this resource"}
              </div>
              <p className="svrs-payer-desc">
                {isFree
                  ? "Enter your details so we can send a copy of the file link to your email."
                  : "Enter your details so we can send the receipt and file access link to your email once payment is confirmed."}
              </p>

              <label htmlFor="svrs-name">Full name</label>
              <input
                id="svrs-name"
                type="text"
                placeholder="e.g. Priya Sharma"
                value={payer.name}
                onChange={(e) => setPayer((p) => ({ ...p, name: e.target.value }))}
                required
              />

              <label htmlFor="svrs-email">Email</label>
              <input
                id="svrs-email"
                type="email"
                placeholder="e.g. priya@example.com"
                value={payer.email}
                onChange={(e) => setPayer((p) => ({ ...p, email: e.target.value.trim() }))}
                required
              />

              <label htmlFor="svrs-phone">Phone number</label>
              <PhoneNumberContainer
                value={payer.phone}
                updateData={(v) => setPayer((p) => ({ ...p, phone: v }))}
                defaultClass="custom-phone-input"
              />
              <span className="svrs-form-hint">We'll only use this to send your file link.</span>

              <button type="submit" className="svrs-lever" disabled={checkingPrior}>
                {checkingPrior ? "Checking…" : isFree ? "Continue" : "Continue to payment"}
              </button>
            </form>
          )}

          {files.length > 0 && !showPayerForm && (
            <button
              type="button"
              className="svrs-lever"
              onClick={handleMainButtonClick}
              disabled={paying || checkingPrior || savingRecord}
            >
              {mainButtonLabel()}
            </button>
          )}

          {/* ── Mounted only while checkout is in progress — its own effect opens Razorpay on mount ── */}
          {paymentPage && (
            <ResourceRazorPay
              email={payer.email}
              name={payer.name}
              contactNumber={payer.phone}
              uId={resource?.userId ?? resource?.serviceProviderUserId ?? null}
              userId={null}
              amount={resource?.amount}
              serviceId={resource?.id ?? resource?.uuid}
              auditUUID={auditUUID}
              updatePaymentStatus={updatePaymentStatus}
            />
          )}

          <div className="svrs-perf svrs-perf--bottom" aria-hidden="true" />
        </div>
      )}
    </div>
  );
};

function formatArrayDate(value) {
  if (Array.isArray(value)) {
    const [y, mo, d, h = 0, mi = 0, s = 0] = value;
    return moment([y, mo - 1, d, h, mi, s]).locale("en").format("MMM D, YYYY");
  }
  const m = moment(value);
  return m.isValid() ? m.locale("en").format("MMM D, YYYY") : "";
}

export default withRouter(ServiceResource);